import tw from 'twin.macro'
import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { CgChevronDown } from 'react-icons/cg'
import { Combobox } from '@headlessui/react'
import * as Portal from '@radix-ui/react-portal'

import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { inputBase } from 'common/components/Input/Input'
import { usePopper } from 'common/hooks/usePopper'
import { exLibDisplayValues } from 'common/constants/exLibConstants'
import { useListenExLibVisibilityQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useListenQuizPublishedAtQuery } from 'modules/AppOnboardingQuiz/appOnboardingQuizApi'
import { useListenCommunityIntroPagesQuery } from '../communityApi'

// The app pages a post button can open. Carried over from announcements: each has
// standard artwork used as the post image when the coach hasn't uploaded one, and
// options only show when the org actually has that page. Names match SCENE_LABELS
// in the mobile app's PostButton, so a coach sees the same wording in both places.
export const availableScenes = [
  {
    value: 'LinkHub',
    displayName: 'Additional Resources',
    defaultImg: 'https://d3ts90okmyjsrp.cloudfront.net/trybe/addl-resources-480.svg',
  },
  {
    value: 'ExerciseLibrary',
    displayName: 'Exercise Library',
    defaultImg: 'https://d3ts90okmyjsrp.cloudfront.net/trybe/ex-library-480.svg',
  },
  {
    value: 'Intro',
    displayName: 'App Quickstart Guide',
    defaultImg: 'https://d3ts90okmyjsrp.cloudfront.net/trybe/quick-start-guide-480.svg',
  },
  {
    value: 'OnboardingQuiz',
    displayName: 'New User Quiz',
    defaultImg: 'https://d3ts90okmyjsrp.cloudfront.net/trybe/quick-start-guide-480.svg',
  },
]

export default function SceneInput({ inputRef: inputRefProp, value, setValue, coachOrgId, error }) {
  // The options container is absolutely positioned and gets its width from this
  // measurement — without it the list renders zero-width (invisible sliver).
  // Measured in a layout effect: at first render the input isn't mounted yet,
  // and render-time measurement stays undefined until something re-renders.
  const localInputRef = useRef()
  const inputRef = inputRefProp || localInputRef
  const [inputWidth, setInputWidth] = useState(undefined)
  useLayoutEffect(() => {
    setInputWidth(inputRef.current?.getBoundingClientRect()?.width)
  }, [inputRef])
  const { data: introPages } = useListenCommunityIntroPagesQuery({ coachOrgId })
  const { data: exLibVisibility } = useListenExLibVisibilityQuery({ coachOrgId })
  const { data: onboardingQuizPublishedAt } = useListenQuizPublishedAtQuery({ coachOrgId })
  const isOnboardingQuizPublished =
    onboardingQuizPublishedAt !== undefined &&
    !onboardingQuizPublishedAt?.isLoading &&
    Boolean(onboardingQuizPublishedAt)
  const introPagesExist = introPages !== undefined && !introPages?.isLoading && Boolean(introPages)

  const comboboxBtnRef = useRef()

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown']
    if (!acceptedCodes.includes(e.code)) {
      e.preventDefault()
    } else if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    }
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const filteredScenes = availableScenes.filter((scene) => {
    if (scene.value === 'ExerciseLibrary') {
      return Boolean(exLibVisibility) && exLibVisibility !== exLibDisplayValues.hidden
    } else if (scene.value === 'Intro') {
      return introPagesExist
    } else if (scene.value === 'OnboardingQuiz') {
      return isOnboardingQuizPublished
    } else {
      return true
    }
  })

  return (
    <div className='mb-4'>
      <label className='capitalize inline-flex cursor-pointer font-semibold text-tBlack' tw='mb-1'>
        App page
      </label>
      <Combobox
        value={value}
        onChange={(val) => {
          setValue('scene', val, { shouldValidate: true })
        }}
      >
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full'>
            <Combobox.Input
              ref={(e) => {
                inputRef.current = e
              }}
              placeholder='Choose app page'
              aria-label='App page'
              autoComplete='off'
              displayValue={(sceneValue) => availableScenes.find((scene) => scene.value === sceneValue)?.displayName}
              onKeyDown={handleInputKeyDown}
              className='text-ellipsis bg-inherit cursor-pointer'
              css={[inputBase, tw`caret-transparent`, error && tw`ring-tRed focus:ring-tRed`]}
            />
            <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
          </Combobox.Button>
          {error && <p className='flex text-xs mt-1 text-tRed'>{error.message}</p>}
        </div>
        <Portal.Root>
          <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
            <SuggestionContainer containerStyle={{ width: inputWidth }}>
              {filteredScenes.map((scene) => (
                <Combobox.Option as={Fragment} key={scene.value} value={scene.value}>
                  {({ active }) => (
                    <SuggestionItem active={active}>
                      <div className='flex items-center'>{scene.displayName}</div>
                    </SuggestionItem>
                  )}
                </Combobox.Option>
              ))}
            </SuggestionContainer>
          </Combobox.Options>
        </Portal.Root>
      </Combobox>
    </div>
  )
}
